'use client';

import React from 'react';
import { useDevice } from '@/app/lib/contexts/DeviceContext';

// Import our organized components and hooks
import { useVideoStream } from './hooks/useVideoStream';
import { useGlobalChatState } from './hooks/useGlobalChatState';
import DesktopChatPage from './frontend/desktop/DesktopChatPage';
import MobileChatPage from './frontend/mobile/MobileChatPage';
import ConnectionIssuePopup from '@/app/popup/ConnectionIssuePopup';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLocale } from '@/app/user-and-session/languages/i18n/locale-provider';
import { metadata } from '@/app/page';
import { initCloudinaryWithPreloadedConfig } from '@/app/lib/cloudinary/cloudinary-image-utils';
import type { Locale } from '@/app/api/chat/systemp_prompt/system_prompt';

export default function ClientChatPage({
  params,
}: {
  params: Promise<{ avatarId: string }>;
}) {
  // Device detection
  const { isMobile, isDesktop } = useDevice();

  // Auth and user data
  const { userEmail } = useAuth();
  const [userAppDataState] = useUserAppData(userEmail || '');

  // Get locale for localized character metadata
  const localeContext = useLocale();
  const locale: Locale | null = (localeContext?.locale === 'en' || localeContext?.locale === 'zh-Hans') 
    ? localeContext.locale 
    : null;

  // Use global chat state for character data and state management
  const [globalState, globalActions] = useGlobalChatState();
  
  // Reset state on mount (only once)
  React.useEffect(() => {
    globalActions.reset();
  }, []); // only run once on mount

  // Reset state when params change (when switching to a different character)
  React.useEffect(() => {
    const resetStateForNewCharacter = async () => {
      const resolvedParams = await params;
      // Clear inactivity timeout state specifically to prevent timers from previous characters
      globalActions.clearInactivityTimeoutState();
      // Reset global state to clear any lingering state from previous character
      globalActions.reset();
    };
    
    resetStateForNewCharacter();
  }, [params, globalActions]);
  
  // Setup video streaming
  const { showConnectionIssuePopup, setShowConnectionIssuePopup, initiateVideoRoom } = useVideoStream();

  // Load character data on mount with optimized metadata caching
  React.useEffect(() => {
    // Preload image and metadata from session storage if they exist to speed up loadingvar isCloudinaryReInit = false;
    initCloudinaryWithPreloadedConfig('dango-cloud');
    const loadCharacterWithCache = async () => {
      const resolvedParams = await params;
      const userId = userAppDataState.userId;
      
      // Check for cached metadata in session storage from discover page
      let cachedMetadata = null;
      try {
        const metadataJson = sessionStorage.getItem(`chat_${resolvedParams.avatarId}_metadata`);
        if (metadataJson) {
          cachedMetadata = JSON.parse(metadataJson);
          console.log('Cached metadata hit:', cachedMetadata);
        }
      } catch (error) {
        console.error('Failed to parse cached metadata:', error);
      }
      // Check for cached chat history in session storage from discover page
      let cachedChatHistory = null;
      try {
        const chatHistoryJson = sessionStorage.getItem(`chat_${resolvedParams.avatarId}_${userId}_chatHistory`);
        if (chatHistoryJson) {
          cachedChatHistory = JSON.parse(chatHistoryJson);
          console.log('Cached chat history hit:', cachedChatHistory);
        }
      } catch (error) {
        console.error('Failed to parse cached chat history:', error);
      }
      // Check for image key in session storage from discover page
      var preloadedImageKey = false;
      // const cachedImageKey = sessionStorage.getItem(`image_${resolvedParams.avatarId}_imageKey`);
      // if (cachedImageKey) {
      //   try {
      //     // Set the active image immediately with the S3 key
      //     console.log('Pre-loading image cache hit:', cachedImageKey);
      //     globalActions.setActiveImage(cachedImageKey);
      //     preloadedImageKey = true;
      //   } catch (error) {
      //     console.error('Failed to pre-load image:', error);
      //   }
      // }
      
      // Load character data with cached metadata if available
      await globalActions.loadCharacter(resolvedParams.avatarId, 
        cachedMetadata, cachedChatHistory, preloadedImageKey, locale);
      // remove cached metadata and chat history from session storage
      sessionStorage.removeItem(`chat_${resolvedParams.avatarId}_metadata`);
      sessionStorage.removeItem(`chat_${resolvedParams.avatarId}_${userId}_chatHistory`);
      sessionStorage.removeItem(`image_${resolvedParams.avatarId}_imageKey`);
    };
    
    loadCharacterWithCache();
  }, [params, globalActions, locale]);

  // Don't render anything until device detection is complete
  if (!isMobile && !isDesktop) {
    return null;
  }

  // Render appropriate frontend based on device
  return (
    <>
      <h1 className="sr-only">Chat with AI Character - Dango Chat</h1>
      {isDesktop && <DesktopChatPage params={params} initiateVideoRoom={initiateVideoRoom} />}
      {isMobile && <MobileChatPage params={params} initiateVideoRoom={initiateVideoRoom} />}
      
      {/* Connection Issue Popup */}
      <ConnectionIssuePopup
        isOpen={showConnectionIssuePopup}
        onClose={() => setShowConnectionIssuePopup(false)}
        type="connection_issue"
      />
    </>
  );
} 